import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import LinearProgress from '@mui/material/LinearProgress';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import { colors } from '../../theme';
import * as subscriptionService from '../../services/subscriptionService';
import * as walletService from '../../services/walletService';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

function remaining(entry) {
  if (!entry || entry.limit == null) return null;
  return Math.max(0, entry.limit - entry.used);
}

function Meter({ label, entry }) {
  const pct = entry?.limit ? Math.min(100, (entry.used / entry.limit) * 100) : 0;
  const over = entry?.limit != null && entry.used >= entry.limit;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.aa.bodyText }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: over ? colors.red[600] : colors.aa.mutedText }}>
          {fmt(entry?.used)} {entry?.limit != null ? `/ ${fmt(entry.limit)}` : '· unlimited'}
        </Typography>
      </Stack>
      {entry?.limit != null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 5, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: over ? colors.red[600] : colors.emerald[600] } }}
        />
      )}
    </Box>
  );
}

export default function UsageMenu() {
  const [usage, setUsage] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [anchor, setAnchor] = useState(null);

  const load = useCallback(() => {
    subscriptionService
      .getUsage()
      .then((res) => setUsage(res.data))
      .catch(() => {});
    walletService
      .getWallet()
      .then((res) => setWallet(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  if (!usage) return null;

  const msgLeft = remaining(usage.messages30d);
  const planName = usage.plan?.name || 'Free';
  const near =
    usage.messages30d?.limit != null && usage.messages30d.used / usage.messages30d.limit >= 0.8;
  const walletOn = !!wallet?.enabled;
  const fmtMoney = (n) => `${wallet?.currency || 'INR'} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <>
      <ButtonBase
        onClick={(e) => {
          setAnchor(e.currentTarget);
          load();
        }}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          border: `1px solid ${colors.aa.border}`,
          gap: 0.75,
          '&:hover': { bgcolor: colors.aa.rowHover },
        }}
      >
        <Chip
          size="small"
          label={planName}
          color={usage.plan?.code && usage.plan.code !== 'free' ? 'success' : 'default'}
          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
        />
        {walletOn ? (
          <Typography sx={{ fontSize: 11.5, color: wallet.low ? colors.red[600] : colors.aa.bodyText, fontWeight: 700 }}>
            {fmtMoney(wallet.balance)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: near ? colors.red[600] : colors.aa.bodyText, fontWeight: 600 }}>
            {msgLeft != null ? `${fmt(msgLeft)} msgs left` : `${fmt(usage.messages30d?.used)} msgs`}
          </Typography>
        )}
      </ButtonBase>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 288, p: 1.75, mt: 0.5 } } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Plan usage</Typography>
          <Chip
            size="small"
            label={planName}
            color={usage.plan?.code && usage.plan.code !== 'free' ? 'success' : 'default'}
            sx={{ height: 20, fontWeight: 700 }}
          />
        </Stack>

        {walletOn && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.25,
              borderRadius: 1,
              bgcolor: wallet.low ? colors.red[50] : colors.emerald[50],
              border: `1px solid ${wallet.low ? colors.red[600] : colors.emerald[600]}22`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.aa.bodyText }}>Wallet balance</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: wallet.low ? colors.red[600] : colors.emerald[700] }}>
                {fmtMoney(wallet.balance)}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 11, color: colors.aa.mutedText }}>
              ~{wallet.messagesRemaining == null ? '∞' : wallet.messagesRemaining.toLocaleString()} messages · {fmtMoney(wallet.messageCost)} each
            </Typography>
          </Box>
        )}

        <Stack spacing={1.5}>
          <Meter label="Messages (30 days)" entry={usage.messages30d} />
          <Meter label="Contacts" entry={usage.contacts} />
          <Meter label="Team members" entry={usage.users} />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {walletOn && (
            <Button
              component={RouterLink}
              to="/wallet"
              onClick={() => setAnchor(null)}
              fullWidth
              size="small"
              variant={wallet.low ? 'contained' : 'outlined'}
              startIcon={<AccountBalanceWalletRoundedIcon />}
            >
              Add funds
            </Button>
          )}
          <Button
            component={RouterLink}
            to="/subscription"
            onClick={() => setAnchor(null)}
            fullWidth
            size="small"
            variant={!walletOn && near ? 'contained' : 'outlined'}
            startIcon={<BoltRoundedIcon />}
          >
            {near ? 'Upgrade' : 'Plan'}
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
